import { Clock, User, MessageSquare, DogIcon } from "lucide-react";

function TileImage({ url, name, quad }) {
  const size = quad ? "h-[17vh] w-[17vh]" : "h-[34vh] w-[34vh]";
  return url ? (
    <img src={url} alt={name} className={`${size} rounded-2xl border-4 border-white object-cover shadow-xl`} onError={(e) => { e.currentTarget.style.visibility = "hidden"; }} />
  ) : (
    <div className={`${size} rounded-2xl border-4 border-white/50 bg-white/30 flex items-center justify-center`}><DogIcon className="h-1/2 w-1/2 text-white" /></div>
  );
}

const names = (n) => (n.animals?.length ? n.animals.map((a) => a.animal_name) : [n.animal_name]).filter(Boolean);
const shadow = { textShadow: "2px 2px 6px rgba(0,0,0,0.35)" };

function PickupTile({ n, order, isNewest, quad }) {
  const dogs = n.animals?.length ? n.animals.slice(0, 2) : [{ image_url: n.image_url, animal_name: n.animal_name }];
  return (
    <div className={`relative h-full rounded-3xl border-4 border-yellow-300 bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 shadow-2xl p-4 flex flex-col items-center justify-center gap-[1.5vh] overflow-hidden ${isNewest ? "ring-8 ring-yellow-200/80" : ""}`}
      data-testid={`tv-alert-tile-${order}`}>
      <span className="absolute top-3 left-3 h-14 w-14 rounded-full bg-black/40 text-white text-3xl font-black flex items-center justify-center border-2 border-white/70" data-testid={`tv-alert-order-${order}`}>
        {order}
      </span>
      <div className={`flex gap-3 ${isNewest ? "animate-bounce" : ""}`}>
        {dogs.map((a, i) => <TileImage key={i} url={a.image_url} name={a.animal_name} quad={quad} />)}
      </div>
      <h2 className={`${quad ? "text-[4.5vh]" : "text-[7vh]"} font-black text-white text-center leading-tight`} style={shadow} data-testid={`tv-alert-names-${order}`}>
        {names(n).join(" & ")}
      </h2>
      <div className="flex flex-wrap justify-center gap-3 text-white">
        <span className="inline-flex items-center gap-2 bg-white/20 rounded-xl px-4 py-1 text-[3vh] font-bold"><User className="h-[3vh] w-[3vh]" />{n.owner_name}</span>
        {n.checkout_time && <span className="inline-flex items-center gap-2 bg-white/20 rounded-xl px-4 py-1 text-[3vh] font-bold"><Clock className="h-[3vh] w-[3vh]" />{n.checkout_time}</span>}
      </div>
    </div>
  );
}

function MessageTile({ n, order }) {
  return (
    <div className="relative h-full rounded-3xl border-4 border-emerald-300 bg-gradient-to-br from-emerald-500 via-teal-500 to-cyan-500 shadow-2xl p-6 flex flex-col items-center justify-center gap-4" data-testid={`tv-alert-tile-${order}`}>
      <span className="absolute top-3 left-3 h-14 w-14 rounded-full bg-black/40 text-white text-3xl font-black flex items-center justify-center border-2 border-white/70">{order}</span>
      <MessageSquare className="h-12 w-12 text-white" />
      <p className="text-[4vh] font-bold text-white text-center" style={shadow}>{n.message}</p>
      <p className="text-xl text-white/90 font-semibold">From: {n.sender_name}</p>
    </div>
  );
}

// 2 alerts → side by side, 3–4 → quad. Numbered in the order staff hit Notify.
export const GoingHomeAlertGrid = ({ alerts, queued, bg }) => {
  const newest = Math.max(...alerts.map((a) => a._seq));
  return (
    <div className={`h-screen h-[100dvh] bg-gradient-to-br ${bg} p-4 flex flex-col gap-3 overflow-hidden`} data-testid="tv-alert-grid">
      <div className="flex items-center justify-center gap-4">
        <h3 className="text-4xl font-black text-white uppercase tracking-wider" style={shadow}>Going Home!</h3>
        <span className="text-2xl font-bold text-white/90 bg-black/30 rounded-xl px-4 py-1" data-testid="tv-alert-count">{alerts.length} pickups</span>
        {queued > 0 && <span className="text-2xl font-bold text-yellow-200 bg-black/30 rounded-xl px-4 py-1" data-testid="tv-alert-queued">+{queued} waiting</span>}
      </div>
      <div className={`flex-1 grid gap-4 min-h-0 grid-cols-2 ${alerts.length > 2 ? "grid-rows-2" : "grid-rows-1"}`}>
        {alerts.map((n, i) => (
          <div key={n._seq} className="min-h-0">
            {n.type === "custom_message"
              ? <MessageTile n={n} order={i + 1} />
              : <PickupTile n={n} order={i + 1} isNewest={n._seq === newest} quad={alerts.length > 2} />}
          </div>
        ))}
      </div>
    </div>
  );
};
